/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useMemo, useContext } from 'react';

import { PrimaryCarousel, ContentBlock, Icon } from '@powdr/components';
import {
  CarouselControlPlacement,
  getIndicatorType,
  handleCustomIndicators,
} from '@powdr/constants';
import { AppContext } from '@powdr/context';
import { useSeason } from '@powdr/hooks';
import { colorProfileByIndex } from '@powdr/utils';

import { StyledContentBlockCarousel, CarouselIcon } from './styles';

export const ContentBlockCarousel = ({
  id = '',
  iconName = '',
  blockType = '',
  customSlideIndicators = 0,
  scrollingIndicators = false,
  slidesToShow = null,
  spaceBetweenSlides = 0,
  colorProfile = null,
  relationships = null,
  parentColorProfile = null,
}) => {
  const {
    isMobile,
  } = useContext(AppContext);
  const { checkSeasonality } = useSeason();
  const { contentBlocks } = relationships;
  const trueColorProfile = (colorProfile !== null)
    ? (typeof colorProfile === 'number') ? colorProfileByIndex(colorProfile) : colorProfile
    : parentColorProfile;
  const slidesToShowParsed = (slidesToShow) ? parseInt(slidesToShow, 10) : 1;

  const contentBlockMap = useMemo(
    () => contentBlocks.map((contentBlock, index) => {
      const { id: blockId, season, isHideBlock } = contentBlock;

      if (checkSeasonality(season) && !isHideBlock) {
        return (
          <ContentBlock
            contentBlock={contentBlock}
            key={blockId}
            index={index}
            columnOption={1}
            blockType={blockType}
            colorProfile={trueColorProfile}
          />
        );
      }

      return null;
    }).filter((x) => x),
    [contentBlocks, blockType, checkSeasonality, trueColorProfile],
  );

  if (contentBlockMap?.length === 0 || !blockType) return null;

  return (
    <StyledContentBlockCarousel key={id}>
      {iconName && (
        <CarouselIcon colorProfile={parentColorProfile}>
          <Icon name={iconName} height="50" />
        </CarouselIcon>
      )}
      <PrimaryCarousel
        colorProfile={parentColorProfile}
        controlSettings={(isMobile || (!isMobile && contentBlockMap.length > slidesToShowParsed))
          && {
            navigation: {
              iconName: 'ui-arrow-circle-left',
              iconSize: 45,
              placement: CarouselControlPlacement.BOTTOM,
            },
            pagination: (handleCustomIndicators(
              contentBlockMap.map((e) => e.props.contentBlock),
              getIndicatorType(customSlideIndicators),
            )?.length > 0) ? {
                type: getIndicatorType(customSlideIndicators),
                placement: CarouselControlPlacement.TOP,
                customBullets: handleCustomIndicators(
                  contentBlockMap.map((e) => e.props.contentBlock),
                  getIndicatorType(customSlideIndicators),
                ),
                scrollingIndicators,
              } : null,
          }}
        slidesPerView={(contentBlockMap.length > slidesToShowParsed)
          ? slidesToShowParsed
          : contentBlockMap.length}
        spaceBetweenSlides={spaceBetweenSlides || 0}
        breakpoints={{
          320: {
            slidesPerView: 1,
          },
          960: {
            slidesPerView: (contentBlockMap.length > slidesToShowParsed)
              ? slidesToShowParsed
              : contentBlockMap.length,
          },
        }}
      >
        {contentBlockMap}
      </PrimaryCarousel>
    </StyledContentBlockCarousel>
  );
};

ContentBlockCarousel.propTypes = {
  id: PropTypes.string,
  iconName: PropTypes.string,
  blockType: PropTypes.string,
  customSlideIndicators: PropTypes.number,
  scrollingIndicators: PropTypes.bool,
  slidesToShow: PropTypes.string,
  spaceBetweenSlides: PropTypes.number,
  colorProfile: PropTypes.number,
  parentColorProfile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  relationships: PropTypes.shape(),
};
